/**
 * useAdminDashboardStats Hook
 *
 * Fetches and transforms global dashboard statistics for admin view
 */

import { useMemo } from 'react';

import { useGetAdminGlobalStatsQuery } from '../../store/api';
import type { GlobalDashboardStats, QualityDistributionData } from '../../types';

interface UseAdminDashboardStatsResult {
  /** Dashboard statistics */
  stats: GlobalDashboardStats | undefined;
  /** Quality distribution data */
  qualityDistribution: QualityDistributionData | undefined;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Computed metrics */
  computed: {
    /** Total flags across all severities */
    totalFlags: number;
    /** Notes with any flags (non-clean) */
    notesWithFlags: number;
    /** Clean rate as percentage */
    cleanRate: number | null;
    /** Average flags per note */
    avgFlagsPerNote: number | null;
  };
}

export const useAdminDashboardStats = (): UseAdminDashboardStatsResult => {
  const { data, isLoading, error, refetch: refetchQuery } = useGetAdminGlobalStatsQuery();

  const refetch = (): void => {
    void refetchQuery();
  };

  const computed = useMemo(() => {
    if (!data) {
      return {
        totalFlags: 0,
        notesWithFlags: 0,
        cleanRate: null,
        avgFlagsPerNote: null,
      };
    }

    const totalFlags =
      (data.total_critical_flags || 0) +
      (data.total_high_flags || 0) +
      (data.total_medium_flags || 0) +
      (data.total_low_flags || 0);

    const totalNotes = data.total_notes_audited || 0;
    const notesClean = data.notes_clean || 0;
    const notesWithFlags = totalNotes - notesClean;

    const cleanRate = totalNotes > 0 ? (notesClean / totalNotes) * 100 : null;
    const avgFlagsPerNote = totalNotes > 0 ? totalFlags / totalNotes : null;

    return {
      totalFlags,
      notesWithFlags,
      cleanRate,
      avgFlagsPerNote,
    };
  }, [data]);

  return {
    stats: data,
    qualityDistribution: data?.quality_distribution,
    isLoading,
    error: error ? 'Failed to load dashboard statistics' : null,
    refetch,
    computed,
  };
};

export default useAdminDashboardStats;

